import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cog6ToothIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { EditableHref, EditableMarkdownField, EditableSelect, EditableText, ReorderButtons, moveArrayItem, useSectionEditing } from '@kevlarcon/pages';
import { fetchOptedInStates } from '../../api/states';
import { useStateMap } from '../../hooks/useStateMap';
import { Arrow } from '../icons';

/** The state tax-credit program's own fixed cap — not marketing copy, so it's
 * hardcoded here rather than a `SectionField` an org admin could edit into
 * something legally wrong (see tenant_site/section_types.py's `TAX_CREDIT`). */
const CREDIT_AMOUNT = '1,700';

// Ids double as the label shown in the style picker, matching
// ThemeSettingsDrawer's Accent field — not OCEN's original 'gold'/
// 'ghost-ivory' terminology, which stays only as the CSS class name.
type TaxCreditCtaVariant = 'accent' | 'outline-ivory';

const TAX_CREDIT_CTA_VARIANTS: TaxCreditCtaVariant[] = ['accent', 'outline-ivory'];
const TAX_CREDIT_CTA_CLASSES: Record<TaxCreditCtaVariant, string> = {
  accent: 'btn btn-gold',
  'outline-ivory': 'btn btn-ghost-ivory',
};

type TaxCreditCta = {
  label: string;
  href: string;
  variant?: TaxCreditCtaVariant;
};

/** Every per-button edit affordance (reorder, style, link, remove) rolled up
 * behind one gear icon, mirroring `@kevlarcon/pages`' `Cta` (see its
 * `CtaGearMenu` in shared.tsx — duplicated here rather than shared because
 * this button pair needs the dark-background preview chip below). Closes
 * itself as soon as a row is used, handing off to that row's own popover
 * (Style/Link) rather than leaving this one stacked behind it. */
function TaxCreditCtaMenu({
  index, variant, href, onDelete, onMoveUp, onMoveDown,
}: {
  index: number;
  variant: TaxCreditCtaVariant;
  href: string;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const testIdBase = `inline-cta-taxcredit-${index}`;

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current?.contains(target)) return;
      // Style/Link's own popover (EditablePopoverField) renders via a portal
      // to document.body, so it's never a DOM descendant of containerRef —
      // without this check, clicking inside it (e.g. picking a style) reads
      // as an "outside" click and unmounts this dropdown (portal included)
      // out from under the click before its own onClick can commit.
      if (target.closest('[data-testid$="-popover"]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '2px' } as const;
  const rowLabelStyle = { fontSize: '11px', color: '#888' } as const;

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        data-testid={`${testIdBase}-menu-button`}
        title="Button options"
        aria-label="Button options"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '20px', height: '20px', padding: 0, border: '1px solid #ccc', borderRadius: '4px',
          background: open ? '#eee' : 'white', color: '#555', cursor: 'pointer',
        }}
      >
        <Cog6ToothIcon width={13} height={13} />
      </button>

      {open && (
        <div
          data-testid={`${testIdBase}-menu-panel`}
          style={{
            position: 'absolute', top: '24px', right: 0, zIndex: 20, minWidth: '150px',
            background: 'white', border: '1px solid #ccc', borderRadius: '6px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)', padding: '6px',
            display: 'flex', flexDirection: 'column', gap: '4px',
          }}
        >
          {(onMoveUp || onMoveDown) && (
            <div style={rowStyle}>
              <span style={rowLabelStyle}>Reorder</span>
              <ReorderButtons onMoveUp={onMoveUp} onMoveDown={onMoveDown} testId={testIdBase} />
            </div>
          )}
          {/* No onClick-to-close here: EditableSelect/EditableHref's own
              popover is a portal mounted while *this* dropdown is still in
              the tree — collapsing `open` on click would unmount them (and
              the just-opened popover) before the click even registers. The
              popover's own full-screen backdrop dims this dropdown anyway;
              the outside-click handler above closes it once the popover
              does. */}
          <div style={rowStyle}>
            <span style={rowLabelStyle}>Style</span>
            <EditableSelect
              value={variant}
              path={['ctas', index, 'variant']}
              options={TAX_CREDIT_CTA_VARIANTS}
              title="Button style"
              triggerTitle="Click to change this button's style"
              renderTrigger={() => variant}
              renderOption={(opt) => (
                // Previewed on a dark chip — `outline-ivory`'s ivory text/
                // border is invisible against the popover's own white
                // background otherwise.
                <span style={{ background: 'var(--burgundy-deep, #3a1420)', padding: '6px 10px', borderRadius: '4px', display: 'inline-block' }}>
                  <span className={TAX_CREDIT_CTA_CLASSES[opt as TaxCreditCtaVariant]} style={{ pointerEvents: 'none' }}>{opt}</span>
                </span>
              )}
            />
          </div>
          <div style={rowStyle}>
            <span style={rowLabelStyle}>Link</span>
            <EditableHref value={href} path={['ctas', index, 'href']} />
          </div>
          {onDelete && (
            <button
              type="button"
              data-testid={`${testIdBase}-remove`}
              onClick={() => { onDelete(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px', border: 'none', background: 'none',
                color: '#c33', cursor: 'pointer', fontSize: '12px', padding: '4px 2px', borderRadius: '4px', textAlign: 'left',
              }}
            >
              <XMarkIcon width={13} height={13} /> Remove button
            </button>
          )}
        </div>
      )}
    </span>
  );
}

/**
 * `TaxCredit`'s buttons sit on the block's dark background, so — unlike every
 * other local section's CTAs — they can't reuse `@kevlarcon/pages`' shared
 * `Cta` (its 'accent'/'outline' pair is styled for a light background; see
 * `tenant_site/section_types.py`'s `TAX_CREDIT_CTA_FIELDS`). Only the first
 * button gets the arrow glyph, matching the design's original static markup.
 * `onDelete` removes this button (only passed when `ctas.length > min_items`
 * — see `TAX_CREDIT_CTA_FIELDS`' `min_items=1`). `onMoveUp`/`onMoveDown`
 * reorder it within `ctas` — since only index 0 gets the arrow glyph above,
 * reordering here actually changes which button looks primary.
 */
function TaxCreditCta({
  label, href, variant = 'accent', index, onDelete, onMoveUp, onMoveDown,
}: TaxCreditCta & { index: number; onDelete?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }) {
  const { active } = useSectionEditing();
  const className = TAX_CREDIT_CTA_CLASSES[variant];
  const handleClick = active ? (e: MouseEvent) => e.preventDefault() : undefined;
  const anchor = (
    <a href={href} className={className} onClick={handleClick}>
      <EditableText as="span" kind="text" value={label} path={['ctas', index, 'label']} />
      {index === 0 && <Arrow />}
    </a>
  );

  if (!active) return anchor;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {anchor}
      <TaxCreditCtaMenu index={index} variant={variant} href={href} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
    </span>
  );
}

type TaxCreditProps = {
  eyebrow?: string;
  heading: string;
  body?: string;
  ctas?: TaxCreditCta[];
  calloutEyebrow?: string;
  amountLabel?: string;
  calloutNote?: string;
};

// `TaxCredit.ctas` has `min_items=1, max_items=2` (`tenant_site.section_types.TAX_CREDIT`).
// The CTA button variants above (`accent`/`outline-ivory`) are tuned for
// this section's historical always-dark backdrop — toggling its background
// (see pages.models.Section.background) to 'light' or 'none' via the CMS's
// `◐` button won't re-tune their contrast automatically.
export default function TaxCredit({
  eyebrow,
  heading,
  body,
  ctas = [],
  calloutEyebrow,
  amountLabel,
  calloutNote,
}: TaxCreditProps) {
  const { active, patchField } = useSectionEditing();
  const request = useStateMap(fetchOptedInStates, []);
  const participating = request.status === 'ready' ? request.data : [];
  const removeCta = (i: number) => patchField(['ctas'], ctas.filter((_, j) => j !== i));
  const addCta = () => patchField(['ctas'], [...ctas, { label: 'Button', href: '#', variant: 'accent' as TaxCreditCtaVariant }]);
  const moveCtaUp = (i: number) => patchField(['ctas'], moveArrayItem(ctas, i, i - 1));
  const moveCtaDown = (i: number) => patchField(['ctas'], moveArrayItem(ctas, i, i + 1));
  const canAddCta = active && ctas.length < 2;
  const canRemoveCta = active && ctas.length > 1;

  return (
    <section className="tax-block section">
      <div className="wrap">
        <div>
          {(eyebrow || active) && (
            <EditableText as="div" className="eyebrow" kind="text" value={eyebrow ?? ''} path={['eyebrow']} />
          )}
          <EditableText as="h2" kind="richtext" value={heading} path={['heading']} />
          {(body || active) && (
            <EditableMarkdownField value={body ?? ''} path={['body']} />
          )}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {ctas.map((cta, i) => (
              <TaxCreditCta
                key={i}
                index={i}
                {...cta}
                onDelete={canRemoveCta ? () => removeCta(i) : undefined}
                onMoveUp={active && i > 0 ? () => moveCtaUp(i) : undefined}
                onMoveDown={active && i < ctas.length - 1 ? () => moveCtaDown(i) : undefined}
              />
            ))}
            {canAddCta && (
              <button
                type="button"
                data-testid="inline-list-ctas-add"
                onClick={addCta}
                style={{ border: '1px dashed rgba(255,255,255,0.5)', background: 'none', color: 'var(--ivory, #eee)', cursor: 'pointer', fontSize: '12px', padding: '8px 14px', borderRadius: '4px' }}
              >
                + Add button
              </button>
            )}
          </div>
        </div>
        <div className="tax-callout">
          {(calloutEyebrow || active) && (
            <EditableText as="div" className="pl-note" kind="text" value={calloutEyebrow ?? ''} path={['calloutEyebrow']} />
          )}
          <div className="pretty-num">
            <small>$</small>
            {CREDIT_AMOUNT}
            <small style={{ fontSize: 18, verticalAlign: 16 }}>/yr</small>
          </div>
          {(amountLabel || active) && (
            <EditableText as="div" className="pretty-lbl" kind="richtext" value={amountLabel ?? ''} path={['amountLabel']} />
          )}
          <hr />
          {(calloutNote || active) && (
            <EditableText as="div" className="pl-note" kind="text" value={calloutNote ?? ''} path={['calloutNote']} />
          )}
          <div className="state-pills">
            {participating.map((feature) => (
              feature.link ? (
                <Link
                  key={feature.abbreviated_name}
                  to={`/pages/state/${feature.abbreviated_name.toLowerCase()}`}
                  data-testid={`taxcredit-state-${feature.abbreviated_name.toLowerCase()}-link`}
                  onClick={active ? (e: MouseEvent) => e.preventDefault() : undefined}
                >
                  {feature.abbreviated_name}
                </Link>
              ) : (
                <span key={feature.abbreviated_name}>{feature.abbreviated_name}</span>
              )
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
